import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-hot-toast'
import { useIsMutating } from '@tanstack/react-query'
import { FixedBottomButton } from '../../../../components'
import { useCryptoConversionValidator } from '../../hooks'
import { useConversionScopedStore } from '../../../../store/scoped'
import { useSendConvertOrSell } from '../../../../domain/transfer/useCases/useSendConvertOrSell'
import { MUTATION_KEYS } from '../../../../constants/mutationKeys'

import { currencyUtils } from '@/utils/currency'

const parseAmountToFloat = currencyUtils.parseAmountToFloat
const formatDynamicCurrency = currencyUtils.formatDynamicCurrency
const formatFiatCurrency = currencyUtils.formatFiatCurrency

const MINIMUM_FIAT_AMOUNT_TO_CALCULATE_FEE = 1.01

export const ConfirmationButton = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()

  // Custom toast for insufficient balance with proper styling
  const showInsufficientBalanceToast = () => {
    toast.error(
      t('api:validationErrors.insufficientBalance', 'Insufficient balance'),
      {
        style: {
          width: 'calc(100vw - 26px)', // Full width minus 13px margins on each side
          marginLeft: '13px',
          marginRight: '13px',
          maxWidth: 'calc(100vw - 26px)',
        },
        icon: '⚠️', // Warning icon instead of red cross
      },
    )
  }

  const { hasEnoughBalance } = useCryptoConversionValidator()
  const { from, to } = useConversionScopedStore((state) => state.conversion)
  const conversionPayload = useConversionScopedStore(
    (state) => state.conversionPayload,
  )
  const lastFeePayload = useConversionScopedStore(
    (state) => state.lastFeePayload,
  )
  const hasErrorOnFeeCalculation = useConversionScopedStore(
    (state) => state.hasErrorOnFeeCalculation,
  )

  const hasDebitAmount =
    parseAmountToFloat({
      amount: from?.cryptoAmount?.amount || '0',
      decimalSeparator: '.',
    }) > 0

  const isCalculatingFee =
    useIsMutating({
      mutationKey: [MUTATION_KEYS.CALCULATE_CONVERSION_FEE],
    }) > 0

  const { sendConvertOrSell, isPending: isSendingConvert } =
    useSendConvertOrSell({
      onSuccess: () => {
        const { credit_amount, credit_currency } = conversionPayload

        const debitAmountFormatted = formatDynamicCurrency({
          amount: from.cryptoAmount.amount,
          currencyCode: from.cryptoAmount.currency,
        })

        const creditFiatAmountFormatted = formatFiatCurrency({
          value: parseAmountToFloat({
            amount: to.fiatAmount?.amount || '0',
            decimalSeparator: '.',
          }),
          fiatCode: to.fiatAmount?.currency || 'USD',
          showDecimals: true,
        })

        const paramsData = {
          title: t(
            'translation:conversionSuccessfully.youConverted',
            'You Converted',
          ),
          amount: String(credit_amount) || '0',
          subtitle: `${debitAmountFormatted} ${t('translation:conversionSuccessfully.to', 'to')}`,
          code: credit_currency || 'USDT',
          label: to.currencyInfoInWallet?.label,
          relativeFiatFormatted: creditFiatAmountFormatted,
        }

        // Navigate to conversion success page
        navigate('/conversion/success', {
          state: paramsData,
        })
      },
      onError: ({ errorMessage }) => {
        // Check if error is about insufficient balance
        if (
          errorMessage?.toLowerCase().includes('insufficient') ||
          errorMessage?.toLowerCase().includes('balance')
        ) {
          showInsufficientBalanceToast()
        } else {
          toast.error(
            errorMessage ||
              t('api:errors.somethingWrong', 'Something went wrong'),
          )
        }
      },
    })

  const checkIfPayloadFeeHasChanged = () => {
    const newFeePayload = {
      credit_currency: to.cryptoAmount.currency,
      debit_amount: from.cryptoAmount.amount,
      debit_currency: from.cryptoAmount.currency,
    }

    return JSON.stringify(lastFeePayload) !== JSON.stringify(newFeePayload)
  }

  const validateConversion = () => {
    // CHECK IF USER HAS ENOUGH BALANCE
    if (
      !hasEnoughBalance({
        debitAmount: from.cryptoAmount.amount,
        availableInWallet: from.currencyInfoInWallet?.amount,
      })
    ) {
      showInsufficientBalanceToast()
      return false
    }

    //IF THE VALUE IS LESS THAN 1.01 USD, SHOW MIN AMOUNT MESSAGE
    if (
      parseAmountToFloat({
        amount: from.fiatAmount?.amount || '0',
        decimalSeparator: '.',
      }) < MINIMUM_FIAT_AMOUNT_TO_CALCULATE_FEE
    ) {
      const minAmountInCrypto =
        1.01 / (from.currencyInfoInWallet?.pricePerUnit || 1)
      const label =
        from.currencyInfoInWallet?.displayCurrencyCode ||
        from.currencyInfoInWallet?.code
      const message = `${t('translation:min', 'Min')}. ${label} ${t('translation:is', 'is')} ${minAmountInCrypto.toFixed(6)}`

      toast.error(message, {
        style: {
          width: 'calc(100vw - 26px)',
          marginLeft: '13px',
          marginRight: '13px',
          maxWidth: 'calc(100vw - 26px)',
        },
        icon: '⚠️',
      })
      return false
    }

    // IF THE ENDPOINT TO CALC FEES FAILS, SHOW A ERROR MESSAGE
    if (
      parseAmountToFloat({
        amount: conversionPayload?.debit_amount || '0',
        decimalSeparator: '.',
      }) <= 0
    ) {
      toast.error(t('api:errors.somethingWrong', 'Something went wrong'))
      return false
    }

    return true
  }

  const handleConfirmConversion = () => {
    const isValid = validateConversion()

    if (!isValid) return

    if (hasErrorOnFeeCalculation) {
      toast.error(t('api:errors.somethingWrong', 'Something went wrong'))
      return
    }

    sendConvertOrSell({
      ...conversionPayload,
      debit_currency: conversionPayload.debit_currency as any,
      credit_currency: conversionPayload.credit_currency as any,
      convert_type: conversionPayload.convert_type as any,
    })
  }

  return (
    <FixedBottomButton
      buttons={{
        label: 'Confirm',
        onPress: handleConfirmConversion,
        disabled:
          isCalculatingFee || !hasDebitAmount || checkIfPayloadFeeHasChanged(),
        isLoading: isSendingConvert,
      }}
    />
  )
}
